import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import { KeyRound, MailCheck } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Link } from '@/lib/router'

import { useForgotPassword, useResetPassword } from './api'
import { lastTenantSlug } from './lastTenant'

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-50/40 px-4 py-12">
      <div className="w-full max-w-sm">{children}</div>
    </div>
  )
}

/**
 * "Forgot password?" — organisation code + employee ID. The API answers the
 * same whether or not the account exists, so this page does too.
 */
export function ForgotPasswordPage() {
  const params = new URLSearchParams(window.location.search)
  const [tenantSlug, setTenantSlug] = useState(params.get('tenant') ?? lastTenantSlug)
  const [empId, setEmpId] = useState(params.get('emp') ?? '')
  const forgot = useForgotPassword()

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    forgot.mutate({ tenantSlug: tenantSlug.trim(), empId: empId.trim() })
  }

  const rateLimited = isAxiosError(forgot.error) && forgot.error.response?.status === 429

  return (
    <Shell>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> Forgot password
          </CardTitle>
          <CardDescription>
            We will email a reset link to the address on your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {forgot.isSuccess ? (
            <Alert variant="positive">
              <MailCheck className="h-4 w-4" />
              <AlertTitle>Check your email</AlertTitle>
              <AlertDescription>
                If that account has an email address on file, a reset link is on its way. It
                works once and expires in 30 minutes. No email? Ask your administrator to reset
                your password.
              </AlertDescription>
            </Alert>
          ) : (
            <form onSubmit={onSubmit} className="space-y-3.5">
              <div className="space-y-1.5">
                <Label htmlFor="fp-tenant">Tenant</Label>
                <Input id="fp-tenant" value={tenantSlug} onChange={(e) => setTenantSlug(e.target.value)}
                  autoCapitalize="none" autoCorrect="off" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fp-emp">Emp ID</Label>
                <Input id="fp-emp" value={empId} onChange={(e) => setEmpId(e.target.value.toUpperCase())}
                  autoCapitalize="characters" required autoFocus={!!tenantSlug} />
              </div>
              {forgot.isError && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {rateLimited
                      ? 'Too many requests. Wait a few minutes and try again.'
                      : 'Could not send the request. Check your connection and try again.'}
                  </AlertDescription>
                </Alert>
              )}
              <Button type="submit" className="w-full" disabled={forgot.isPending}>
                {forgot.isPending ? 'Sending…' : 'Email me a reset link'}
              </Button>
            </form>
          )}
          <Link to="/login" className="block text-center text-xs text-navy-700 underline">
            Back to sign in
          </Link>
        </CardContent>
      </Card>
    </Shell>
  )
}

/** Opened from the emailed link: /reset-password?tenant=…&token=… */
export function ResetPasswordPage() {
  const navigate = useNavigate()
  const params = new URLSearchParams(window.location.search)
  const tenantSlug = params.get('tenant') ?? ''
  const token = params.get('token') ?? ''
  const reset = useResetPassword()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')

  const tooShort = password.length > 0 && password.length < 8
  const mismatch = confirm.length > 0 && confirm !== password
  const canSubmit = password.length >= 8 && confirm === password && !!token && !!tenantSlug

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    reset.mutate({ tenantSlug, token, newPassword: password })
  }

  const linkInvalid =
    !token || !tenantSlug ||
    (isAxiosError(reset.error) &&
      (reset.error.response?.data as { code?: string } | undefined)?.code === 'reset_link_invalid')

  return (
    <Shell>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> Choose a new password
          </CardTitle>
          <CardDescription>At least 8 characters. You will be signed out everywhere.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {reset.isSuccess ? (
            <>
              <Alert variant="positive">
                <AlertTitle>Password changed</AlertTitle>
                <AlertDescription>Sign in with your new password, on the web or the app.</AlertDescription>
              </Alert>
              <Button className="w-full" onClick={() => navigate({ to: '/login' })}>Go to sign in</Button>
            </>
          ) : linkInvalid ? (
            <Alert variant="destructive">
              <AlertTitle>This link is invalid or has expired</AlertTitle>
              <AlertDescription>
                Reset links work once and expire after 30 minutes.{' '}
                <Link to="/forgot-password" className="underline">Request a new one</Link>.
              </AlertDescription>
            </Alert>
          ) : (
            <form onSubmit={onSubmit} className="space-y-3.5">
              <div className="space-y-1.5">
                <Label htmlFor="rp-new">New password</Label>
                <Input id="rp-new" type="password" value={password} autoComplete="new-password"
                  onChange={(e) => setPassword(e.target.value)} required autoFocus />
                {tooShort && <p className="text-2xs text-destructive">Use at least 8 characters.</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rp-confirm">Confirm new password</Label>
                <Input id="rp-confirm" type="password" value={confirm} autoComplete="new-password"
                  onChange={(e) => setConfirm(e.target.value)} required />
                {mismatch && <p className="text-2xs text-destructive">The passwords do not match.</p>}
              </div>
              {reset.isError && (
                <Alert variant="destructive">
                  <AlertDescription>Could not change the password. Try again.</AlertDescription>
                </Alert>
              )}
              <Button type="submit" className="w-full" disabled={!canSubmit || reset.isPending}>
                {reset.isPending ? 'Saving…' : 'Set new password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </Shell>
  )
}
